import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import {
  CalculateOverlayPositionInterface,
  DrawImageCoverInterface,
  MergeCoordinatePositionInterface,
  MergeImageInterface,
  RoundRectInterface,
} from '../types';

@Injectable()
export class ImageMergeService {
  merge(data: MergeImageInterface): Observable<File> {
    return from(this.mergeAsync(data));
  }

  private mergeAsync(data: MergeImageInterface): Promise<File> {
    return new Promise((resolve, reject) => {
      const baseImg = new Image();
      const baseUrl = URL.createObjectURL(data.baseImage);

      let overlayImg: HTMLImageElement | null = null;
      let overlayUrl: string | null = null;

      if (data.overlayImage && data.config.overlay) {
        overlayImg = new Image();
        overlayUrl = URL.createObjectURL(data.overlayImage);
      }

      let baseLoaded = false;
      let overlayLoaded = !data.overlayImage;

      const cleanup = () => {
        URL.revokeObjectURL(baseUrl);
        if (overlayUrl) URL.revokeObjectURL(overlayUrl);
      };

      const tryMerge = () => {
        if (!baseLoaded || !overlayLoaded) return;

        const scale = data.config.scale || 2;
        const canvasWidth = data.config.width;
        const canvasHeight = data.config.height;

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth * scale;
        canvas.height = canvasHeight * scale;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        ctx.scale(scale, scale);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        if (data.config.backgroundColor) {
          ctx.fillStyle = data.config.backgroundColor;
          ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        }

        if (data.config.borderRadius) {
          ctx.beginPath();
          this.roundRect({
            ctx,
            x: 0,
            y: 0,
            width: canvasWidth,
            height: canvasHeight,
            radius: data.config.borderRadius,
          });
          ctx.clip();

          if (data.config.backgroundColor) {
            ctx.fillStyle = data.config.backgroundColor;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);
          }
        }

        this.drawImageCover({
          ctx,
          img: baseImg,
          x: 0,
          y: 0,
          width: canvasWidth,
          height: canvasHeight,
        });

        if (overlayImg && data.config.overlay) {
          const { x, y } = this.calculateOverlayPosition({
            canvasWidth,
            canvasHeight,
            overlay: data.config.overlay,
          });

          ctx.save();

          if (data.config.overlay.borderRadius) {
            ctx.beginPath();
            this.roundRect({
              ctx,
              x,
              y,
              width: data.config.overlay.width,
              height: data.config.overlay.height,
              radius: data.config.overlay.borderRadius,
            });
            ctx.clip();
          }

          if (data.config.backgroundColor) {
            ctx.fillStyle = data.config.backgroundColor;
            ctx.fillRect(
              x,
              y,
              data.config.overlay.width,
              data.config.overlay.height,
            );
          }

          this.drawImageCover({
            ctx,
            img: overlayImg,
            x,
            y,
            width: data.config.overlay.width,
            height: data.config.overlay.height,
          });

          ctx.restore();

          if (
            data.config.overlay.borderColor &&
            data.config.overlay.borderWidth
          ) {
            const borderWidth = data.config.overlay.borderWidth;
            const inset = borderWidth / 2;
            const radius = Math.max(
              0,
              (data.config.overlay.borderRadius || 0) - inset,
            );

            ctx.save();
            ctx.beginPath();
            this.roundRect({
              ctx,
              x: x + inset,
              y: y + inset,
              width: data.config.overlay.width - borderWidth,
              height: data.config.overlay.height - borderWidth,
              radius,
            });
            ctx.strokeStyle = data.config.overlay.borderColor;
            ctx.lineWidth = borderWidth;
            ctx.stroke();
            ctx.restore();
          }
        }

        cleanup();

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'merged-image.png', {
                type: 'image/png',
              });
              resolve(file);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/png',
          1,
        );
      };

      baseImg.onload = () => {
        baseLoaded = true;
        tryMerge();
      };

      baseImg.onerror = () => {
        cleanup();
        reject(new Error('Failed to load base image'));
      };

      if (overlayImg && overlayUrl) {
        overlayImg.onload = () => {
          overlayLoaded = true;
          tryMerge();
        };

        overlayImg.onerror = () => {
          cleanup();
          reject(new Error('Failed to load overlay image'));
        };

        overlayImg.src = overlayUrl;
      }

      baseImg.src = baseUrl;
    });
  }

  private calculateOverlayPosition(
    data: CalculateOverlayPositionInterface,
  ): MergeCoordinatePositionInterface {
    let x: number;
    let y: number;

    switch (data.overlay.position) {
      case 'top-left':
        x = data.overlay.offsetX;
        y = data.overlay.offsetY;
        break;
      case 'top-right':
        x = data.canvasWidth - data.overlay.width - data.overlay.offsetX;
        y = data.overlay.offsetY;
        break;
      case 'bottom-left':
        x = data.overlay.offsetX;
        y = data.canvasHeight - data.overlay.height - data.overlay.offsetY;
        break;
      case 'bottom-right':
        x = data.canvasWidth - data.overlay.width - data.overlay.offsetX;
        y = data.canvasHeight - data.overlay.height - data.overlay.offsetY;
        break;
      case 'center':
        x = (data.canvasWidth - data.overlay.width) / 2 + data.overlay.offsetX;
        y =
          (data.canvasHeight - data.overlay.height) / 2 + data.overlay.offsetY;
        break;
    }

    return { x, y };
  }

  private drawImageCover(data: DrawImageCoverInterface): void {
    const imgAspect = data.img.width / data.img.height;
    const targetAspect = data.width / data.height;

    let srcX = 0;
    let srcY = 0;
    let srcW = data.img.width;
    let srcH = data.img.height;

    if (imgAspect > targetAspect) {
      srcW = data.img.height * targetAspect;
      srcX = (data.img.width - srcW) / 2;
    } else {
      srcH = data.img.width / targetAspect;
      srcY = (data.img.height - srcH) / 2;
    }

    data.ctx.drawImage(
      data.img,
      srcX,
      srcY,
      srcW,
      srcH,
      data.x,
      data.y,
      data.width,
      data.height,
    );
  }

  private roundRect(data: RoundRectInterface): void {
    data.ctx.moveTo(data.x + data.radius, data.y);
    data.ctx.lineTo(data.x + data.width - data.radius, data.y);
    data.ctx.quadraticCurveTo(
      data.x + data.width,
      data.y,
      data.x + data.width,
      data.y + data.radius,
    );
    data.ctx.lineTo(data.x + data.width, data.y + data.height - data.radius);
    data.ctx.quadraticCurveTo(
      data.x + data.width,
      data.y + data.height,
      data.x + data.width - data.radius,
      data.y + data.height,
    );
    data.ctx.lineTo(data.x + data.radius, data.y + data.height);
    data.ctx.quadraticCurveTo(
      data.x,
      data.y + data.height,
      data.x,
      data.y + data.height - data.radius,
    );
    data.ctx.lineTo(data.x, data.y + data.radius);
    data.ctx.quadraticCurveTo(data.x, data.y, data.x + data.radius, data.y);
    data.ctx.closePath();
  }
}
